import React from 'react';

/** Nach dieser Zeit ohne `dragover` gilt der Drag als beendet (verlorene `dragleave`-Events). */
const DRAG_IDLE_TIMEOUT_MS = 700;

/**
 * Datei-Drop auf der ganzen Seite statt nur in einer Drop-Zone.
 * Liefert `dragActive` für ein Overlay; `onFiles` läuft nur, wenn kein anderer
 * Handler den Drop schon übernommen hat (eigene Drop-Zonen rufen `preventDefault`).
 */
export function useWindowFileDrop({
  enabled,
  onFiles,
  ownDropZoneSelector,
}: {
  enabled: boolean;
  onFiles: (files: File[]) => void;
  /** Über diesen Elementen bleibt das Overlay aus, weil sie ihr eigenes Drop-Feedback zeigen. */
  ownDropZoneSelector?: string;
}): boolean {
  const [dragActive, setDragActive] = React.useState(false);
  const onFilesRef = React.useRef(onFiles);
  onFilesRef.current = onFiles;

  React.useEffect(() => {
    if (!enabled) {
      setDragActive(false);
      return;
    }

    let idleTimer = 0;

    const isFileDrag = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const isOverOwnDropZone = (event: DragEvent) =>
      Boolean(
        ownDropZoneSelector &&
          event.target instanceof Element &&
          event.target.closest(ownDropZoneSelector),
      );

    const stop = () => {
      window.clearTimeout(idleTimer);
      idleTimer = 0;
      setDragActive(false);
    };

    // Drop-Zonen stoppen die Propagation ihrer Drag-Events, darum in der Capture-Phase erkennen.
    const handleDragOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      // Ohne preventDefault lehnt der Browser den Drop ab und öffnet die Datei im Tab.
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      setDragActive(!isOverOwnDropZone(event));
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(stop, DRAG_IDLE_TIMEOUT_MS);
    };

    const handleDragLeave = (event: DragEvent) => {
      // relatedTarget === null: der Zeiger hat das Fenster verlassen.
      if (isFileDrag(event) && !event.relatedTarget) stop();
    };

    const handleDrop = (event: DragEvent) => {
      stop();
      if (!isFileDrag(event) || event.defaultPrevented) return;
      event.preventDefault();
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length > 0) onFilesRef.current(files);
    };

    window.addEventListener('dragover', handleDragOver, true);
    window.addEventListener('dragleave', handleDragLeave, true);
    window.addEventListener('dragend', stop, true);
    // Bubble-Phase: nur so ist erkennbar, ob eine eigene Drop-Zone den Drop schon übernommen hat.
    window.addEventListener('drop', handleDrop);
    return () => {
      window.clearTimeout(idleTimer);
      window.removeEventListener('dragover', handleDragOver, true);
      window.removeEventListener('dragleave', handleDragLeave, true);
      window.removeEventListener('dragend', stop, true);
      window.removeEventListener('drop', handleDrop);
    };
  }, [enabled, ownDropZoneSelector]);

  return dragActive;
}
